import { useTeams } from '@/domains/teams';
import { Button } from '@/shared/ui/button';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';
import { X } from 'lucide-react';
import { useState } from 'react';
import { useJobTitles } from '../hooks/useUsers';

export interface UserFilterValues {
  name: string;
  email: string;
  jobTitle: string;
  team: string;
  activeJobsMin: string;
  activeJobsMax: string;
  completedJobsMin: string;
  completedJobsMax: string;
}

interface UsersFiltersModalProps {
  isOpen: boolean;
  onClose: () => void;
  nameFilter: string;
  emailFilter: string;
  jobTitleFilter: string;
  teamFilter: string;
  activeJobsMin: string;
  activeJobsMax: string;
  completedJobsMin: string;
  completedJobsMax: string;
  onApply: (filters: UserFilterValues) => void;
  onClear: () => void;
}

const EMPTY_FILTERS: UserFilterValues = {
  name: '',
  email: '',
  jobTitle: 'all',
  team: 'all',
  activeJobsMin: '',
  activeJobsMax: '',
  completedJobsMin: '',
  completedJobsMax: '',
};

export function UsersFiltersModal({
  isOpen,
  onClose,
  nameFilter,
  emailFilter,
  jobTitleFilter,
  teamFilter,
  activeJobsMin,
  activeJobsMax,
  completedJobsMin,
  completedJobsMax,
  onApply,
  onClear,
}: UsersFiltersModalProps) {
  const [local, setLocal] = useState<UserFilterValues>({
    name: nameFilter,
    email: emailFilter,
    jobTitle: jobTitleFilter,
    team: teamFilter,
    activeJobsMin,
    activeJobsMax,
    completedJobsMin,
    completedJobsMax,
  });
  const { data: jobTitlesData } = useJobTitles();
  const { data: teamsData } = useTeams();

  if (!isOpen) return null;

  const handleApply = () => {
    onApply(local);
  };

  const handleClear = () => {
    setLocal(EMPTY_FILTERS);
    onClear();
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 dark:bg-black/40 z-40 backdrop-blur-sm"
        onClick={onClose}
        onKeyDown={(e) => e.key === 'Escape' && onClose()}
        role="button"
        tabIndex={0}
        aria-label="Close filter panel"
      />

      <div className="fixed top-0 right-0 h-full w-[400px] bg-card border-l border-border shadow-2xl z-50 flex flex-col animate-in slide-in-from-right duration-300">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div>
            <h3 className="font-semibold text-foreground text-lg">Filter Users</h3>
            <p className="text-sm text-muted-foreground mt-1">Refine your user list</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onClose} className="h-8 w-8 p-0">
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Filters Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Name Filter */}
          <div>
            <Label className="text-foreground font-medium mb-2 block">Name</Label>
            <Input
              placeholder="Search by name..."
              value={local.name}
              onChange={(e) => setLocal({ ...local, name: e.target.value })}
              className="bg-background border-border text-foreground"
            />
          </div>

          {/* Email Filter */}
          <div>
            <Label className="text-foreground font-medium mb-2 block">Email</Label>
            <Input
              placeholder="Search by email..."
              value={local.email}
              onChange={(e) => setLocal({ ...local, email: e.target.value })}
              className="bg-background border-border text-foreground"
            />
          </div>

          {/* Role / Job Title Filter */}
          <div>
            <Label className="text-foreground font-medium mb-2 block">Role</Label>
            <Select
              value={local.jobTitle}
              onValueChange={(v) => setLocal({ ...local, jobTitle: v })}
            >
              <SelectTrigger className="bg-background border-border text-foreground">
                <SelectValue placeholder="Select role..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="all">All Roles</SelectItem>
                {(jobTitlesData?.values ?? []).map((jt: { id: number; name: string }) => (
                  <SelectItem key={jt.id} value={jt.name}>
                    {jt.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Team Filter */}
          <div>
            <Label className="text-foreground font-medium mb-2 block">Team</Label>
            <Select value={local.team} onValueChange={(v) => setLocal({ ...local, team: v })}>
              <SelectTrigger className="bg-background border-border text-foreground">
                <SelectValue placeholder="Select team..." />
              </SelectTrigger>
              <SelectContent className="bg-popover border-border">
                <SelectItem value="all">All Teams</SelectItem>
                {(teamsData ?? []).map((t) => (
                  <SelectItem key={t.id} value={t.name}>
                    {t.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Active Jobs Range */}
          <div>
            <Label className="text-foreground font-medium mb-2 block">Active Jobs</Label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min"
                value={local.activeJobsMin}
                onChange={(e) => setLocal({ ...local, activeJobsMin: e.target.value })}
                className="bg-background border-border text-foreground"
              />
              <Input
                type="number"
                placeholder="Max"
                value={local.activeJobsMax}
                onChange={(e) => setLocal({ ...local, activeJobsMax: e.target.value })}
                className="bg-background border-border text-foreground"
              />
            </div>
          </div>

          {/* Completed Jobs Range */}
          <div>
            <Label className="text-foreground font-medium mb-2 block">Completed Jobs</Label>
            <div className="grid grid-cols-2 gap-3">
              <Input
                type="number"
                placeholder="Min"
                value={local.completedJobsMin}
                onChange={(e) => setLocal({ ...local, completedJobsMin: e.target.value })}
                className="bg-background border-border text-foreground"
              />
              <Input
                type="number"
                placeholder="Max"
                value={local.completedJobsMax}
                onChange={(e) => setLocal({ ...local, completedJobsMax: e.target.value })}
                className="bg-background border-border text-foreground"
              />
            </div>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="p-6 border-t border-border flex-shrink-0 flex items-center justify-between bg-card/50">
          <Button variant="outline" onClick={handleClear} className="border-border hover:bg-accent">
            Clear All
          </Button>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={onClose} className="border-border hover:bg-accent">
              Cancel
            </Button>
            <Button onClick={handleApply} className="bg-orange-600 hover:bg-orange-700 text-white">
              Apply Filters
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
